import { useCallback } from "react";

import {
  useCellReviewStore,
  type CellReviewFlagType,
  type CellReviewIdentityRequest,
  type CellReviewResponseRecord,
  type CellReviewStoreResult,
} from "../shared/cellReviews/useCellReviewStore";
import { cellReviewsQueryKey } from "../shared/cellReviews/fetchCellReviews";

// For the "response" scope a cell has no proposal plan; it is identified by
// carrier + plan-group column. The column's planGroupId rides in the identity's
// coverageType slot (an opaque string on the shared cell-review entity); the
// carrier is in benefitKey.
const identityKeyOf = (review: CellReviewResponseRecord): string =>
  `${review.benefitKey}::${review.coverageType ?? ""}`;

interface UseResponsesCellReviewsOptions {
  rfpId: string | undefined;
  enabled?: boolean;
  onSaveError?: (error: Error) => void;
}

/**
 * Adapts the shared cell-review store to the Responses grid, where a cell has
 * no proposal plan — it is identified by carrier + plan-group column. Persists
 * under the "response" cell-review scope (planId = carrierId, fieldPath =
 * planGroupId, carried in the identity's coverageType slot).
 */
export const useResponsesCellReviews = ({
  rfpId,
  enabled = true,
  onSaveError,
}: UseResponsesCellReviewsOptions): CellReviewStoreResult => {
  const buildIdentity = useCallback(
    (
      planId: string,
      fieldPath: string,
      reviewType: CellReviewFlagType,
    ): CellReviewIdentityRequest => ({
      scope: { type: "response", rfpId },
      coverageType: fieldPath,
      benefitKey: planId,
      reviewType,
    }),
    [rfpId],
  );

  return useCellReviewStore({
    rfpId,
    queryKey: cellReviewsQueryKey("response", rfpId),
    listUrl: rfpId
      ? `/rfps/${encodeURIComponent(rfpId)}/cell-reviews?scopeType=response`
      : undefined,
    mutateUrl: rfpId
      ? `/rfps/${encodeURIComponent(rfpId)}/cell-reviews`
      : undefined,
    enabled,
    identityKeyOf,
    buildIdentity,
    onSaveError,
  });
};
